import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Bell, Mail, MessageSquare, Phone, Loader2, PlayCircle, History } from 'lucide-react';

const EVENT_OPTIONS = [
  { key: 'training', label: 'Attestati formazione in scadenza', icon: '🎓' },
  { key: 'medical',  label: 'Visite mediche in scadenza', icon: '🩺' },
  { key: 'dvr',      label: 'DVR / DUVRI in scadenza', icon: '📄' },
  { key: 'pratiche', label: 'Pratiche tecniche (CILA/SCIA/Insegne)', icon: '🏢' },
];

export default function NotificationSettingsPage() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [log, setLog] = useState([]);

  const load = async () => {
    try {
      setLoading(true);
      const [s, l] = await Promise.all([api.get('/notifications/settings'), api.get('/notifications/log?limit=10').catch(() => ({ data: [] }))]);
      setSettings(s.data);
      setLog(l.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore caricamento');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/notifications/settings', settings);
      toast.success('Impostazioni salvate');
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
    finally { setSaving(false); }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await api.post('/notifications/run-now?only_me=true');
      toast.success(`✅ ${r.data.events_found} eventi, ${r.data.sent} inviati, ${r.data.skipped_dup} duplicati`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
    finally { setRunning(false); }
  };

  if (loading || !settings) return <p>Caricamento…</p>;

  const setChannel = (ch, v) => setSettings({ ...settings, channels: { ...settings.channels, [ch]: v } });
  const toggleEvent = (k) => setSettings({
    ...settings,
    events_enabled: settings.events_enabled.includes(k)
      ? settings.events_enabled.filter(x => x !== k)
      : [...settings.events_enabled, k],
  });

  return (
    <div className="space-y-6 max-w-3xl" data-testid="notif-settings-page">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <Bell className="w-7 h-7 text-amber-600" /> Notifiche & Promemoria
        </h1>
        <p className="text-sm text-slate-600 mt-1">Sistema multi-canale per avvisare i clienti delle scadenze. Cron automatico ogni mattina alle 8:00 (Europe/Rome).</p>
      </motion.div>

      <div className="bg-white rounded-lg shadow-sm border p-6 space-y-4">
        <h2 className="text-lg font-semibold flex items-center gap-2"><Mail className="w-5 h-5" /> Canali (priorità: email → WhatsApp → SMS)</h2>
        <div className="space-y-2">
          {[
            { k: 'email', label: 'Email (gratis con Resend)', icon: <Mail className="w-4 h-4" /> },
            { k: 'whatsapp', label: 'WhatsApp (~€0,03/msg, richiede Twilio)', icon: <MessageSquare className="w-4 h-4 text-emerald-600" /> },
            { k: 'sms', label: 'SMS (~€0,06/msg, richiede Twilio)', icon: <Phone className="w-4 h-4 text-blue-600" /> },
          ].map(c => (
            <label key={c.k} className="flex items-center gap-3 cursor-pointer hover:bg-slate-50 p-2 rounded">
              <Checkbox checked={settings.channels?.[c.k] || false} onCheckedChange={(v) => setChannel(c.k, !!v)} data-testid={`channel-${c.k}`} />
              {c.icon}<span className="text-sm">{c.label}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border p-6 space-y-3">
        <h2 className="text-lg font-semibold">Eventi monitorati</h2>
        {EVENT_OPTIONS.map(ev => (
          <label key={ev.key} className="flex items-center gap-3 cursor-pointer hover:bg-slate-50 p-2 rounded">
            <Checkbox checked={settings.events_enabled?.includes(ev.key)} onCheckedChange={() => toggleEvent(ev.key)} data-testid={`event-${ev.key}`} />
            <span className="text-xl">{ev.icon}</span>
            <span className="text-sm">{ev.label}</span>
          </label>
        ))}
        <p className="text-xs text-slate-500 mt-2">Soglie di preavviso: <strong>{(settings.thresholds_days || []).join(' / ')} giorni</strong> prima della scadenza.</p>
      </div>

      <div className="bg-white rounded-lg shadow-sm border p-6 space-y-3">
        <h2 className="text-lg font-semibold">Stato cron giornaliero</h2>
        <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-sm text-emerald-900">
          ✅ Cron attivo ogni giorno alle <strong>08:00</strong> (Europe/Rome). Scansiona certificati, visite, DVR e pratiche e invia notifiche secondo le impostazioni sopra.
        </div>
        <div className="flex gap-2 pt-2">
          <label className="flex items-center gap-2 mr-auto cursor-pointer">
            <Checkbox checked={settings.enabled} onCheckedChange={v => setSettings({ ...settings, enabled: !!v })} data-testid="enable-notifications" />
            <span className="text-sm">Notifiche abilitate per il mio account</span>
          </label>
          <Button onClick={runNow} disabled={running} variant="outline" data-testid="run-now-btn">
            {running ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <PlayCircle className="w-4 h-4 mr-1" />}
            Esegui ora
          </Button>
          <Button onClick={save} disabled={saving} data-testid="save-settings">{saving ? 'Salvataggio…' : 'Salva impostazioni'}</Button>
        </div>
      </div>

      {log.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h2 className="text-lg font-semibold flex items-center gap-2 mb-3"><History className="w-5 h-5" /> Ultimi invii ({log.length})</h2>
          <div className="space-y-1 text-xs">
            {log.map(l => (
              <div key={l.id} className="flex justify-between border-b py-1">
                <span>{l.event?.item_title} <span className="text-slate-500">→ {l.event?.personnel_name}</span></span>
                <span className="text-slate-500">{l.sent_at ? new Date(l.sent_at).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
